import React, { useState, useCallback, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Container, Button } from '@mui/material';
import TemplateCard from '../components/Dashboard/TemplateCard';
import Pagination from '../components/Dashboard/Pagination';
import templateService from '../services/templateService';
import sesService from '../services/sesService';

const Home = () => {
  const [templates, setTemplates] = useState([]);
  const [dailyUsage, setDailyUsage] = useState(null);
  const [page, setPage] = useState(1);
  const templatesPerPage = 6;

  // Cache keys for localStorage
  const CACHE_KEY_TEMPLATES = 'cachedTemplates';
  const CACHE_KEY_USAGE = 'cachedDailyUsage';

  // Load cached data from localStorage (if available)
  const loadCache = () => {
    const cachedTemplates = localStorage.getItem(CACHE_KEY_TEMPLATES);
    const cachedUsage = localStorage.getItem(CACHE_KEY_USAGE);
    if (cachedTemplates) {
      setTemplates(JSON.parse(cachedTemplates));
    }
    if (cachedUsage) {
      setDailyUsage(JSON.parse(cachedUsage));
    }
  };

  // Fetch data from API and update both state and cache
  const fetchData = useCallback(async () => {
    try {
      const usage = await sesService.getDailyUsage();
      const data = await templateService.getTemplates();
      setDailyUsage(usage);
      setTemplates(data);
      localStorage.setItem(CACHE_KEY_USAGE, JSON.stringify(usage));
      localStorage.setItem(CACHE_KEY_TEMPLATES, JSON.stringify(data));
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  }, []);

  // Load cached data on mount
  useEffect(() => {
    loadCache();
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const displayedTemplates = templates.slice(
    (page - 1) * templatesPerPage,
    page * templatesPerPage
  );
  const totalPages = Math.ceil(templates.length / templatesPerPage);

  const handleEdit = (template) => {
    window.location.href = `/template-editor?id=${template.id}`;
  };

  const handleDelete = async (templateId) => {
    try {
      await templateService.deleteTemplate(templateId);
      // Refresh the data after deletion (and update cache)
      await fetchData();
    } catch (err) {
      console.error("Error deleting template:", err);
    }
  };

  // Common style for the usage/stat cards
  const usageCardStyle = {
    p: 4,
    textAlign: 'center',
    minWidth: '220px',
    backgroundColor: '#1E1E1E',
    color: '#FFFFFF',
    border: '1px solid #333',
    borderRadius: 2,
    boxShadow: '0 0 10px rgba(0, 255, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#00ff00',
      boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
    },
  };

  return (
    <Container sx={{ py: 4 }}>
      {/* Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={fetchData}>
          Refresh Email Templates
        </Button>
      </Box>

      {/* Daily Usage and Total Templates Cards */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 4,
          mb: 4,
        }}
      >
        {dailyUsage && (
          <>
            <Paper sx={usageCardStyle}>
              <Typography variant="subtitle1">Emails sent (24h)</Typography>
              <Typography variant="h3">{dailyUsage.sent}</Typography>
            </Paper>
            <Paper sx={usageCardStyle}>
              <Typography variant="subtitle1">Remaining sends</Typography>
              <Typography variant="h3">{dailyUsage.remaining}</Typography>
            </Paper>
            <Paper sx={usageCardStyle}>
              <Typography variant="subtitle1">Sending quota used</Typography>
              <Typography variant="h3">{dailyUsage.usedPercent}%</Typography>
            </Paper>
          </>
        )}
        <Paper sx={usageCardStyle}>
          <Typography variant="subtitle1">Total Templates</Typography>
          <Typography variant="h3">{templates.length}</Typography>
        </Paper>
      </Box>

      {/* Template Cards Grid */}
      <Grid container spacing={4} justifyContent="center">
        {displayedTemplates.map((template) => (
          <Grid item key={template.id} xs={12} sm={6} md={4} lg={4}>
            <TemplateCard
              template={template}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Pagination count={totalPages} page={page} onChange={handlePageChange} />
      </Box>
    </Container>
  );
};

export default Home;
